/**
 * Returns the requested image resolution variant ('thumb' | 'medium' | 'full')
 * for AWS S3 CloudFront URLs generated by Lambda compression.
 *
 * Variants:
 * - 'thumb': ~200px (ideal for avatars, navbar icons, badges, dropdowns)
 * - 'medium': ~800px (ideal for grid preview tiles, card listings, mobile viewports)
 * - 'full': ~1920px (ideal for full-screen lightbox / crop view)
 *
 * @param {string} url - Original image URL
 * @param {'thumb' | 'medium' | 'full'} variant - Desired variant
 * @returns {string} - Transformed URL
 */
export function getMediaVariant(url, variant = 'full') {
  if (!url || typeof url !== 'string') return url;
  if (url.includes('/images/full/') || url.includes('/images/medium/') || url.includes('/images/thumb/')) {
    return url.replace(/\/images\/(full|medium|thumb)\//, `/images/${variant}/`);
  }
  return url;
}

/**
 * Image error handler to fall back from 'thumb' or 'medium' variant to 'full' image on HTTP 403/404 fetch error.
 *
 * @param {React.SyntheticEvent<HTMLImageElement, Event>} e - Image error event
 * @param {string|function|null} [secondaryFallback=null] - Optional secondary image URL or fallback callback if full also fails
 */
export function handleImageError(e, secondaryFallback = null) {
  const img = e?.currentTarget || e?.target;
  if (!img) return;

  const currentSrc = img.src || '';

  // If current URL is a thumb or medium variant, fallback to full variant
  if (currentSrc.includes('/images/thumb/') || currentSrc.includes('/images/medium/')) {
    img.onerror = typeof secondaryFallback === 'function'
      ? secondaryFallback
      : secondaryFallback
        ? () => { img.onerror = null; img.src = secondaryFallback; }
        : null;
    img.src = getMediaVariant(currentSrc, 'full');
    return;
  }

  // Secondary fallback if provided
  if (typeof secondaryFallback === 'function') {
    img.onerror = null;
    secondaryFallback(e);
  } else if (secondaryFallback && img.src !== secondaryFallback) {
    img.onerror = null;
    img.src = secondaryFallback;
  }
}
